import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Button as BaseButton, ContextMenu, Table } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { ReactComponent as LedgerEmptyIcon } from '@ubeya/shared-web/assets/recent-actions.svg';
import { formatDate } from '../leaveFormatters';
import { formatLedgerAmount, resolveLedgerEventType, resolveLedgerReason } from '../ledgerFormatters';
import { MiniEmpty, MiniEmptyHint, MiniEmptyTitle, NumericCell } from './styled';

const LedgerLoadMoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0 4px;
`;

const LedgerLoadMoreButton = styled(BaseButton)`
  padding: 6px 14px;
`;

const LedgerCountHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey4};
`;

// Synthetic preview rows for today's in-progress TOIL delta. The cron only writes
// DailyAccrual events for completed days; today is layered on at read time, so
// the balance card shows the live value but the ledger would otherwise be empty
// for today. Synthesize a non-persistent row so admins can see the calculation.
const buildToilTodayPreviewRows = (management) => {
  if (!management?.balances?.length) return [];
  const today = new Date();
  return management.balances
    .filter((balance) => balance?.accrualMode === 'toil-monthly')
    .map((balance) => ({
      id: `toil-today-preview-${balance.accountContractV2LeaveId}`,
      eventType: 'toil-today-preview',
      occurredAt: today,
      leaveTypeName: balance.leaveTypeName,
      amount: Number(balance.toilTodayDelta),
      unit: 'hours',
      metadata: { source: 'toil-today-preview' },
      isPreview: true
    }));
};

const LedgerTab = ({ ledger, ledgerTotal, management, onReverseAdjust, onLoadMore, hasMore, isLoadingMore }) => {
  const { t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();

  const previewRows = useMemo(() => buildToilTodayPreviewRows(management), [management]);
  const combinedLedger = useMemo(() => [...previewRows, ...(ledger || [])], [previewRows, ledger]);

  const columns = useMemo(
    () => [
      {
        Header: t('employeeLeave_history_col_date'),
        dataKey: 'occurredAt',
        Cell: ({ rowData: { occurredAt } }) => <>{formatDate(formattingMap, occurredAt)}</>
      },
      {
        Header: t('leaveType'),
        dataKey: 'leaveTypeName',
        Cell: ({ rowData: { leaveTypeName } }) => <>{leaveTypeName}</>
      },
      {
        Header: t('employeeLeave_history_col_type'),
        dataKey: 'eventType',
        Cell: ({ rowData }) => {
          if (rowData.eventType === 'toil-today-preview')
            return <>{t('employeeLeave_ledger_event_toilTodayPreview')}</>;
          return <>{resolveLedgerEventType(rowData, t)}</>;
        }
      },
      {
        Header: t('employeeLeave_history_col_change'),
        dataKey: 'amount',
        Cell: ({ rowData: { amount, unit } }) => {
          const value = Number(amount ?? 0);
          const tone = value > 0 ? 'positive' : value < 0 ? 'negative' : undefined;
          return <NumericCell $tone={tone}>{formatLedgerAmount(value, unit, formattingMap, t)}</NumericCell>;
        }
      },
      {
        Header: t('employeeLeave_history_col_reason'),
        dataKey: 'reason',
        Cell: ({ rowData: { eventType, metadata } }) => {
          if (eventType === 'toil-today-preview') return <>{t('employeeLeave_ledger_reason_toilTodayPreview')}</>;
          const reason = resolveLedgerReason(metadata, t);
          return <>{reason || '-'}</>;
        }
      },
      {
        Header: '',
        dataKey: 'actions',
        width: 48,
        // Only AdminAdjust is reversible — ledger is append-only, reverse writes a compensating row.
        Cell: ({ rowData }) => {
          if (rowData.eventType !== 'admin-adjust') return null;
          if (!onReverseAdjust) return null;
          return (
            <ContextMenu
              hideIconBackground
              usePortal
              openFromRight
              options={[{ label: t('employeeLeave_ledger_reverse'), onClick: () => onReverseAdjust(rowData) }]}
            />
          );
        }
      }
    ],
    [t, formattingMap, onReverseAdjust]
  );

  if (combinedLedger.length === 0) {
    return (
      <MiniEmpty>
        <LedgerEmptyIcon />
        <MiniEmptyTitle>{t('employeeLeave_emptyLedger_title')}</MiniEmptyTitle>
        <MiniEmptyHint>{t('employeeLeave_emptyLedger_hint')}</MiniEmptyHint>
      </MiniEmpty>
    );
  }

  return (
    <>
      <Table
        isVirtualized={false}
        headerRowHeight={50}
        autoHeight
        data={combinedLedger}
        columns={columns}
        shadow={false}
      />
      {hasMore && (
        <LedgerLoadMoreRow>
          <LedgerLoadMoreButton type="button" secondary narrow disabled={isLoadingMore} onClick={onLoadMore}>
            {isLoadingMore ? t('loading') : t('employeeLeave_ledger_loadMore')}
          </LedgerLoadMoreButton>
          {ledgerTotal !== undefined && (
            <LedgerCountHint>
              {t('employeeLeave_ledger_showingOf', { shown: ledger.length, total: ledgerTotal })}
            </LedgerCountHint>
          )}
        </LedgerLoadMoreRow>
      )}
    </>
  );
};

export default LedgerTab;
